import React, { useState, useEffect } from 'react';
import Carousel from '../components/Carousel';
import { GraduationCap, BookOpen, Users, Award, ArrowRight, Globe, Loader2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const homeSections = {
  celebracionGrammy: {
    visible: false
  },
  convenioMarco: {
    visible: false
  }
};

const Home = () => {
  const [convocatorias, setConvocatorias] = useState([]);
  const [noticias, setNoticias] = useState([]);
  const [homeContent, setHomeContent] = useState({
    mainTitle: '',
    mainDescription: '',
    aboutCenid: '',
    participants: [],
    convenioText: ''
  });
  const [convenioOpen, setConvenioOpen] = useState(false);
  const [celebracionOpen, setCelebracionOpen] = useState(false);
  const [showCelebracionImage, setShowCelebracionImage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentNoticiaIndex, setCurrentNoticiaIndex] = useState(0);

  // Cargar datos desde JSON
  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar datos del carousel (convocatorias y noticias)
        const carouselResponse = await fetch(`/data/carousel.json?v=${Date.now()}`);
        const carouselData = await carouselResponse.json();

        // Cargar contenido del home
        const homeResponse = await fetch(`/data/home-content.json?v=${Date.now()}`);
        const homeData = await homeResponse.json();

        // Filtrar y ordenar convocatorias visibles
        const convocatoriasVisibles = (carouselData.convocatorias || [])
          .filter(conv => conv.visible)
          .sort((a, b) => a.orden - b.orden);

        // Filtrar y ordenar noticias visibles
        const noticiasVisibles = (carouselData.noticias || [])
          .filter(noticia => noticia.visible)
          .sort((a, b) => a.orden - b.orden);

        setConvocatorias(convocatoriasVisibles);
        setNoticias(noticiasVisibles);
        setHomeContent(homeData);
        setIsLoading(false);
      } catch (error) {
        console.error('Error cargando datos:', error);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Navegación del carousel de noticias
  const goToNextNoticia = () => {
    setCurrentNoticiaIndex((prev) => (prev + 1) % noticias.length);
  };

  const goToPrevNoticia = () => {
    setCurrentNoticiaIndex((prev) => (prev - 1 + noticias.length) % noticias.length);
  };

  // Auto-play del carousel de noticias
  useEffect(() => {
    if (noticias.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentNoticiaIndex((prev) => (prev + 1) % noticias.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [noticias.length, currentNoticiaIndex]);

  // Obtener icono según el nombre
  const getIconComponent = (iconName) => {
    const icons = {
      Award,
      GraduationCap,
      Globe,
      BookOpen,
      Users
    };
    return icons[iconName] || Award;
  };

  return (
    <div className="min-h-screen">

      {/* Carousel Section */}
      <Carousel fitMode="contain" />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">

        {/* Celebración / Anuncio Section */}
        {homeSections.celebracionGrammy.visible && (
        <section className="mb-12">
          <div className="max-w-7xl mx-auto px-6 py-8">

            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 border-t-4 border-[#0b742f] relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-48 h-48 bg-gradient-to-br from-[#0b742f]/6 to-transparent rounded-full"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-4 mb-2">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center uppercase">DOS FIGURAS DEL CENID HACEN HISTORIA EN LOS GRAMMY®</h2>
                  <button
                    onClick={() => setCelebracionOpen(prev => !prev)}
                    className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-full shadow-sm transition-colors"
                    aria-expanded={celebracionOpen}
                  >
                    {celebracionOpen ? (
                      <>
                        <span className="text-sm font-semibold">Ocultar</span>
                        <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-semibold">Mostrar</span>
                        <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>

                <div className="md:col-span-3">
                  <p className="text-gray-700 leading-relaxed mb-6 text-lg text-center mt-4">
                    La <strong>Dra. Claudia Correa</strong> y el <strong>Dr. Antonio Vergara</strong>, <strong>artistas</strong>, <strong>productores</strong>, <strong>ingenieros</strong>, <strong>académicos</strong> e <strong>investigadores</strong> del CENID, logran nominaciones al <strong>GRAMMY</strong> americano y latino, elevando el nombre de <strong>Iberoamérica</strong> desde el arte y la academia.
                  </p>
                </div>

                {celebracionOpen && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center mt-8 animate-fade-in">
                    <div className="md:col-span-2">
                      <p className="text-gray-700 leading-relaxed">
                        El Centro de Investigación y Desarrollo (CENID) celebra con orgullo un acontecimiento histórico para su comunidad académica: dos de sus miembros, la Dra. Claudia Correa y el Dr. Antonio Vergara, han alcanzado hitos sin precedentes en la industria musical internacional al obtener nominaciones oficiales tanto en los GRAMMY® americanos como en los Latin GRAMMY®, posicionando el CENID en un escenario global donde convergen la educación, la investigación y la excelencia artística iberoamericana.
                      </p>

                      <p className="text-gray-700 leading-relaxed mt-4">
                        Estas distinciones, otorgadas por la Recording Academy® (GRAMMY®) y la Latin Recording Academy® (Latin GRAMMY®), representan el más alto reconocimiento al mérito musical, técnico y creativo.
                      </p>

                      <p className="text-gray-700 leading-relaxed mt-4">
                        Y es precisamente desde el CENID —institución mexicana dedicada a la formación, la investigación y la innovación educativa— que estas dos figuras emergen como referentes ineludibles del diálogo entre arte, cultura y academia.
                      </p>

                      <h3 className="text-lg font-semibold text-gray-900 mt-6">UN VÍNCULO ESPECIAL CON MÉXICO: LA NOMINACIÓN AL LATIN GRAMMY® POR UN ÁLBUM DE MÚSICA TEJANA</h3>

                      <p className="text-gray-700 leading-relaxed mt-3">
                        Uno de los hechos más significativos para la comunidad mexicana del CENID es que la nominación al Latin GRAMMY® obtenida por Antonio y Claudia se debe al trabajo técnico que ambos realizaron —mezcla y masterización— para un álbum de música tejana, género profundamente arraigado en el norte de México y el sur de Estados Unidos.
                      </p>

                      <p className="text-gray-700 leading-relaxed mt-3">
                        Que un álbum dentro del campo de la música regional mexicana haya sido llevado al terreno del Latin GRAMMY® gracias al trabajo profesional de dos investigadores del CENID constituye un motivo de celebración y orgullo institucional.
                      </p>

                      <div className="mt-6">
                        <Link to="/celebracion-grammy" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-5 py-3 rounded-lg font-semibold hover:opacity-95 transition-opacity">
                          Conoce más este importante evento
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>

                    <div className="md:col-span-1 flex items-center justify-center">
                      <figure className="bg-white rounded-lg p-0 shadow-md border border-gray-100 w-full max-w-xs cursor-pointer overflow-hidden" onClick={() => setShowCelebracionImage(true)}>
                        <div className="w-full h-80 md:h-96 bg-gray-50 flex items-center justify-center overflow-hidden">
                          <img
                            src="/img_cenid/celebracion_claudia_antonio.jpg"
                            alt="Dra. Claudia Correa y Dr. Antonio Vergara"
                            className="h-full w-auto object-contain mx-auto"
                            onError={(e) => { e.target.onerror = null; e.target.src = 'https://recursos.cenid.org/img_cenid/grammy_dres.jpg'; }}
                          />
                        </div>
                        <figcaption className="mt-3 text-sm text-gray-600 text-center p-3">Dra. Claudia Correa & Dr. Antonio Vergara</figcaption>
                      </figure>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Convenio Section */}
        {homeSections.convenioMarco.visible && (
        <section className="mb-16 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-t-4 border-[#0b742f] relative overflow-hidden">
            {/* Decorative background */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#0b742f]/5 to-transparent rounded-full -mr-48 -mt-48"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#e87729]/5 to-transparent rounded-full -ml-32 -mb-32"></div>

            <div className="relative">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] p-3 rounded-xl shadow-lg">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  {homeContent.mainTitle}
                </h1>
                <button
                  onClick={() => setConvenioOpen(prev => !prev)}
                  className="ml-auto inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-full shadow-sm transition-colors"
                  aria-expanded={convenioOpen}
                  aria-controls="convenio-content"
                >
                  {convenioOpen ? (
                    <>
                      <span className="text-sm font-semibold">Ocultar</span>
                      <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-semibold">Mostrar</span>
                      <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              {/* Logos Section */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-200 mb-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#0b742f]/5 via-transparent to-[#e87729]/5"></div>
                <div className="relative flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
                  {/* TecNM Logo */}
                  <div className="group flex flex-col items-center">
                    <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform group-hover:scale-105 border-2 border-transparent hover:border-[#0b742f]/20">
                      <img
                        src="https://recursos.cenid.org/img_cenid/tecnm.jpg"
                        alt="TecNM"
                        className="h-24 md:h-32 w-auto object-contain"
                      />
                    </div>
                    <span className="mt-3 text-sm font-semibold text-gray-600 group-hover:text-[#0b742f] transition-colors">Tecnológico Nacional de México</span>
                  </div>

                  {/* Separator with icon */}
                  <div className="flex items-center justify-center">
                    <div className="hidden md:flex flex-col items-center gap-2">
                      <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#0b742f] to-transparent"></div>
                      <div className="bg-[#e87729] p-2 rounded-full shadow-lg">
                        <Award className="w-5 h-5 text-white" />
                      </div>
                      <div className="w-px h-12 bg-gradient-to-b from-[#0b742f] via-transparent to-transparent"></div>
                    </div>
                    <div className="md:hidden w-32 h-px bg-gradient-to-r from-transparent via-[#0b742f] to-transparent"></div>
                  </div>

                  {/* CENID Logo */}
                  <div className="group flex flex-col items-center">
                    <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform group-hover:scale-105 border-2 border-transparent hover:border-[#e87729]/20">
                      <img
                        src="https://recursos.cenid.org/img_cenid/cenid.png"
                        alt="CENID"
                        className="h-24 md:h-32 w-auto object-contain"
                      />
                    </div>
                    <span className="mt-3 text-sm font-semibold text-gray-600 group-hover:text-[#e87729] transition-colors">Centro de Estudios e Investigación</span>
                  </div>
                </div>
              </div>
            </div>
            <div id="convenio-content">
              {convenioOpen && (
                <>
                  <div
                    className="text-gray-700 leading-relaxed mb-6"
                    dangerouslySetInnerHTML={{ __html: homeContent.mainDescription }}
                  />

                  <div className="bg-gray-50 rounded-lg p-6 mt-6">
                    <h3 className="font-semibold text-[#0b742f] mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      En este significativo acto participaron:
                    </h3>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
                      {homeContent.participants.map((participant, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className={index % 2 === 0 ? 'text-[#0b742f]' : 'text-[#e87729]'}>▪</span>
                          {participant}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className="text-gray-700 leading-relaxed mt-6 border-t pt-6"
                    dangerouslySetInnerHTML={{ __html: homeContent.convenioText }}
                  />
                </>
              )}
            </div>
          </div>
        </section>
        )}

        {/* About CENID Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <GraduationCap className="w-10 h-10 text-[#e87729]" />
                <h2 className="text-2xl md:text-3xl font-bold">Sobre CENID</h2>
              </div>
              <div
                className="text-lg leading-relaxed text-white/95"
                dangerouslySetInnerHTML={{ __html: homeContent.aboutCenid }}
              />
            </div>
          </div>
        </section>

        {/* Convocatorias Section */}
        {convocatorias.length > 0 && (
          <section>
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-[#0b742f]" />
                Convocatorias CENID
              </h2>
              <div className="h-1 w-24 bg-[#e87729] rounded"></div>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-[#0b742f] animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {convocatorias.map((convocatoria) => (
                  <Link
                    key={convocatoria.id}
                    to={convocatoria.link}
                    className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-[#0b742f] hover:-translate-y-1"
                  >
                    <div className="p-6">
                      <div className="bg-[#0b742f]/5 w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#0b742f] transition-colors duration-300">
                        <ArrowRight className="w-6 h-6 text-[#0b742f] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#0b742f] transition-colors duration-300">
                        {convocatoria.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {convocatoria.description}
                      </p>
                      <div className="mt-4 flex items-center text-[#0b742f] font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                        Más información
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Carousel de Noticias */}
        {noticias.length > 0 && (
          <section className="mt-16">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 relative min-h-[400px]">
              {isLoading ? (
                <div className="flex justify-center items-center py-16">
                  <Loader2 className="w-8 h-8 text-[#0b742f] animate-spin" />
                </div>
              ) : (
                <>
                  <div className="p-8 md:p-12 relative min-h-[400px]">
                    {noticias.map((noticia, index) => {
                      const IconComponent = getIconComponent(noticia.icono);
                      const isActive = index === currentNoticiaIndex;

                      return (
                        <div
                          key={noticia.id}
                          className={`transition-all duration-700 ${isActive ? 'opacity-100 relative' : 'opacity-0 absolute inset-0 pointer-events-none'
                            }`}
                        >
                          <div>
                            <div className="flex items-center gap-4 mb-6">
                              <div
                                className="p-3 rounded-lg"
                                style={{ backgroundColor: noticia.color }}
                              >
                                <IconComponent className="w-8 h-8 text-white" />
                              </div>
                              <div>
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                                  {noticia.titulo}
                                </h2>
                                {noticia.subtitulo && (
                                  <p className="text-gray-600 text-sm mt-1">
                                    {noticia.subtitulo}
                                  </p>
                                )}
                              </div>
                            </div>

                            {noticia.issn && (
                              <p className="text-sm text-gray-600 mb-4 font-semibold">
                                {noticia.issn}
                              </p>
                            )}

                            <div
                              className="text-gray-700 leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: noticia.descripcion }}
                            />

                            {noticia.link && noticia.link !== '#' && (
                              <Link
                                to={noticia.link}
                                className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-md"
                                style={{
                                  backgroundColor: noticia.color,
                                  color: 'white'
                                }}
                              >
                                Más información
                                <ArrowRight className="w-4 h-4" />
                              </Link>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Controles del carousel si hay más de una noticia */}
                  {noticias.length > 1 && (
                    <>
                      {/* Botones de navegación */}
                      <button
                        onClick={goToPrevNoticia}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10"
                        aria-label="Noticia anterior"
                      >
                        <ChevronLeft className="w-6 h-6 text-gray-700" />
                      </button>

                      <button
                        onClick={goToNextNoticia}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10"
                        aria-label="Siguiente noticia"
                      >
                        <ChevronRight className="w-6 h-6 text-gray-700" />
                      </button>

                      {/* Indicadores */}
                      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                        {noticias.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentNoticiaIndex(index)}
                            className={`h-2 rounded-full transition-all duration-300 ${index === currentNoticiaIndex
                              ? 'w-8 bg-[#0b742f]'
                              : 'w-2 bg-gray-300 hover:bg-gray-400'
                              }`}
                            aria-label={`Ir a noticia ${index + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </section>
        )}
      </div>
      {/* Celebración image modal */}
      {showCelebracionImage && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setShowCelebracionImage(false)}>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <button className="absolute top-3 right-3 bg-white/90 text-gray-700 rounded-full p-2 shadow-md hover:bg-white z-20" onClick={() => setShowCelebracionImage(false)}>✕</button>
            </div>

            <div className="bg-white rounded-lg overflow-hidden p-6 shadow-lg">
              <img src="/img_cenid/celebracion_claudia_antonio.jpg" alt="Celebración CENID" className="w-full object-contain mx-auto" style={{ maxHeight: 'calc(80vh - 48px)' }} onError={(e) => { e.target.onerror = null; e.target.src = 'https://recursos.cenid.org/img_cenid/grammy_dres.jpg'; }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
